/**
 * Feb 27, 2026 · Purchase Return
 *
 * Warehouse-facing screen that ships QC-Rejected raw material back to
 * the supplier. Source picker filters strictly on Rejected lots, form
 * gates reason (mandatory), and the History + Supplier Summary tabs
 * expose the audit trail for QBRs.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import api from "@/lib/api";
import {
  ArrowUUpLeftIcon,
  MagnifyingGlassIcon,
  ArrowsClockwiseIcon,
  ClipboardTextIcon,
  StackIcon,
  UsersFourIcon,
  TruckIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const money = (n) => Number(n || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const TABS = [
  { key: "new",     label: "New Return",       icon: ArrowUUpLeftIcon },
  { key: "history", label: "Return History",   icon: ClipboardTextIcon },
  { key: "summary", label: "Supplier Summary", icon: UsersFourIcon },
];

export default function PurchaseReturns() {
  const [tab, setTab] = useState("new");

  // ── Source picker state ───────────────────────────────────────
  const [mode, setMode] = useState("rejected");   // "rejected" | "excess"
  const [search, setSearch] = useState("");
  const [rejected, setRejected] = useState([]);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [picked, setPicked] = useState(null);

  // ── Form state ────────────────────────────────────────────────
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [lrNo, setLrNo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // ── History + Summary state ───────────────────────────────────
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ rows: [], totals: {} });
  const [loadingSummary, setLoadingSummary] = useState(false);

  const loadRejected = async () => {
    setLoadingRejected(true);
    try {
      const endpoint = mode === "excess" ? "/purchase-returns/excess-lots" : "/purchase-returns/rejected-lots";
      const { data } = await api.get(endpoint, { params: search ? { search } : {} });
      setRejected(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load source lots"); }
    finally { setLoadingRejected(false); }
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get("/purchase-returns/");
      setHistory(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load history"); }
  };
  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.get("/purchase-returns/summary");
      setSummary(data || { rows: [], totals: {} });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load summary"); }
    finally { setLoadingSummary(false); }
  };
  useEffect(() => { loadRejected(); loadHistory(); loadSummary(); }, []);
  useEffect(() => {
    const t = setTimeout(loadRejected, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, mode]);

  const resetForm = () => { setPicked(null); setQty(""); setReason(""); setLrNo(""); setNotes(""); };

  const submit = async () => {
    if (!picked) { toast.error("Pick a rejected batch first"); return; }
    const n = Number(qty);
    if (!(n > 0)) { toast.error("Return Qty must be > 0"); return; }
    if (n - picked.returnable_balance > 1e-4) { toast.error(`Cannot return more than the returnable balance (${fmt(picked.returnable_balance)} ${picked.uom})`); return; }
    if ((reason || "").trim().length < 4) { toast.error("Reason for Return is mandatory (min 4 characters)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-returns/", {
        lot_id: picked.lot_id,
        qty: n,
        reason: reason.trim(),
        transporter_lr_no: lrNo.trim(),
        notes: notes.trim(),
      });
      toast.success(`${data.return_no} posted · ${data.lot_status === "Returned" ? "batch closed" : `balance ${fmt(data.returnable_balance)} ${picked.uom}`}`);
      resetForm();
      loadRejected(); loadHistory(); loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setSaving(false); }
  };

  const inpCls =
    "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

  const grandTotal = useMemo(() => summary.rows?.reduce?.((s, r) => s + Number(r.total_qty || 0), 0) || 0, [summary]);

  return (
    <div className="p-6 space-y-5" data-testid="purchase-returns-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <ArrowUUpLeftIcon size={11} weight="bold" />
            Purchase &amp; Inventory · Purchase Return
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Purchase Return</h1>
          <div className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Send QC-Rejected raw material back to the supplier. Every return posts an audit-preserving OUT
            transaction on the RM Store, decrements the batch balance, and links back to the original
            GRN + Supplier Invoice.
          </div>
        </div>
        <button onClick={() => { loadRejected(); loadHistory(); loadSummary(); }} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="pr-refresh">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`pr-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 h-9 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px flex items-center gap-1.5 ${
                tab === t.key
                  ? "border-[var(--accent)] text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Icon size={12} weight="bold" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === "new" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Rejected batch picker */}
          <div className="lg:col-span-2 tactical-card p-4 space-y-3" data-testid="pr-picker">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                <StackIcon size={13} weight="bold" /> {mode === "excess" ? "Excess (Held for Return) Batches" : "Rejected Batches (QC-gated)"}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex border border-[var(--border)] rounded-sm overflow-hidden" data-testid="pr-mode">
                  {[["rejected","Rejected"],["excess","Excess"]].map(([k,l]) => (
                    <button key={k} data-testid={`pr-mode-${k}`} onClick={() => { setMode(k); setPicked(null); }}
                      className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest ${
                        mode === k ? "bg-[var(--accent)]/20 text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"
                      }`}>{l}</button>
                  ))}
                </div>
                <div className="relative min-w-[220px]">
                  <MagnifyingGlassIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    data-testid="pr-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by batch or supplier invoice…"
                    className="w-full h-8 pl-8 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="pr-rejected-table">
                <thead className="bg-[var(--bg-2)] sticky top-0 z-10">
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left px-3 py-2">Batch</th>
                    <th className="text-left px-3 py-2">RM Code</th>
                    <th className="text-left px-3 py-2">Supplier · Invoice</th>
                    <th className="text-left px-3 py-2">QC Notes</th>
                    <th className="text-right px-3 py-2">Balance</th>
                    <th className="text-right px-3 py-2 w-16">—</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRejected && <tr><td colSpan={6} className="text-center py-6 text-zinc-500">Loading…</td></tr>}
                  {!loadingRejected && rejected.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-500">{mode === "excess" ? "No excess lots held for return." : "No rejected batches — floor is clean."}</td></tr>}
                  {rejected.map(r => (
                    <tr key={r.lot_id}
                        data-testid={`pr-lot-${r.lot_id}`}
                        onClick={() => { setPicked(r); setQty(String(r.returnable_balance)); }}
                        className={`border-t border-[var(--border)] cursor-pointer ${picked?.lot_id === r.lot_id ? "bg-[var(--accent)]/10" : "hover:bg-[var(--bg-2)]/40"}`}>
                      <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.batch_no}</td>
                      <td className="px-3 py-2 font-mono text-purple-300 truncate max-w-[220px]" title={r.rm_code}>{r.rm_code}</td>
                      <td className="px-3 py-2 text-zinc-300">
                        {r.supplier_name}
                        <div className="text-[10px] font-mono text-zinc-500">{r.invoice_no || "—"} · {r.received_date}</div>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-amber-300/80 max-w-[240px] truncate" title={r.quality_notes}>{r.quality_notes || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(r.returnable_balance)} {r.uom}</td>
                      <td className="px-3 py-2 text-right">
                        <button data-testid={`pr-pick-${r.lot_id}`}
                          className="h-7 px-2 border border-[var(--accent)]/60 text-[var(--accent)] hover:bg-[var(--accent)]/10 rounded-sm text-[10px] font-mono uppercase tracking-widest">Pick</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Form */}
          <div className="tactical-card p-4 space-y-3" data-testid="pr-form">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
              <TruckIcon size={13} weight="bold" /> New Return
            </div>
            {!picked && <div className="text-xs text-zinc-500 py-8 text-center">Pick a rejected batch on the left to start.</div>}
            {picked && (
              <>
                <div className="border border-dashed border-[var(--border)] p-3 text-[11px] font-mono space-y-0.5 rounded-sm">
                  <div className="text-zinc-500">BATCH · <span className="text-[var(--accent)]">{picked.batch_no}</span></div>
                  <div className="text-zinc-400 truncate" title={picked.rm_code}>RM: {picked.rm_code}</div>
                  <div className="text-zinc-400">SUPPLIER: <span className="text-zinc-200">{picked.supplier_name}</span></div>
                  <div className="text-zinc-400">INVOICE: <span className="text-zinc-200">{picked.invoice_no || "—"}</span> · {picked.invoice_date || "—"}</div>
                  <div className="text-zinc-400">SOURCE GODOWN: <span className="text-zinc-200">{picked.godown_name}</span></div>
                  <div className="text-zinc-400">RETURNABLE: <span className="text-emerald-300">{fmt(picked.returnable_balance)} {picked.uom}</span></div>
                  <div className="text-zinc-400 text-[10px]">RATE: {money(picked.unit_cost)} / {picked.uom}</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Return Qty · max {fmt(picked.returnable_balance)} {picked.uom}</label>
                  <input data-testid="pr-qty" type="number" step="0.001" value={qty} onChange={e => setQty(e.target.value)} className={`${inpCls} font-mono text-right`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Reason for Return <span className="text-red-400">*</span></label>
                  <textarea data-testid="pr-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Shade not matching STD · Contamination · Wrong count…"
                    className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Transporter LR No</label>
                  <input data-testid="pr-lr" value={lrNo} onChange={e => setLrNo(e.target.value)} placeholder="e.g. VRL-24-98765" className={inpCls} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Notes (optional)</label>
                  <input data-testid="pr-notes" value={notes} onChange={e => setNotes(e.target.value)} className={inpCls} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={resetForm} className="h-9 px-3 border border-[var(--border)] rounded-sm text-xs uppercase tracking-widest hover:bg-[var(--bg-2)]">Cancel</button>
                  <button data-testid="pr-submit" onClick={submit} disabled={saving}
                    className="h-9 px-4 bg-[var(--accent)] hover:bg-[var(--accent)]/90 disabled:opacity-40 text-black font-mono text-xs uppercase tracking-widest rounded-sm">
                    {saving ? "Posting…" : "Post Return"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="tactical-card overflow-x-auto" data-testid="pr-history">
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]">
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-3 py-2">PR No</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-left px-3 py-2">RM</th>
                <th className="text-left px-3 py-2">Supplier · Invoice</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">LR No</th>
                <th className="text-left px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-zinc-500">No returns posted yet.</td></tr>}
              {history.map(r => (
                <tr key={r.id} data-testid={`pr-hist-${r.id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                  <td className="px-3 py-2 text-right"><TwoLineDocNo value={r.return_no} className="text-[var(--accent)]" /></td>
                  <td className="px-3 py-2 text-zinc-400 font-mono">{r.return_date}</td>
                  <td className="px-3 py-2 font-mono text-purple-300">{r.batch_no}</td>
                  <td className="px-3 py-2 text-zinc-300 truncate max-w-[200px]" title={r.rm_code}>{r.rm_code}</td>
                  <td className="px-3 py-2 text-zinc-300">{r.supplier_name}<div className="text-[10px] text-zinc-500 font-mono">{r.invoice_no || "—"}</div></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(r.qty)} {r.uom}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{money(r.value)}</td>
                  <td className="px-3 py-2 text-zinc-400 font-mono text-[10px]">{r.transporter_lr_no || "—"}</td>
                  <td className="px-3 py-2 text-zinc-500 max-w-[280px] truncate" title={r.reason}>{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "summary" && (
        <div className="tactical-card" data-testid="pr-summary">
          <div className="px-4 py-2 border-b border-[var(--border)] text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex flex-wrap gap-4">
            <span>{summary.totals?.suppliers || 0} supplier{(summary.totals?.suppliers || 0) === 1 ? "" : "s"}</span>
            <span>· {summary.totals?.returns_count || 0} return{(summary.totals?.returns_count || 0) === 1 ? "" : "s"}</span>
            <span>· <span className="text-amber-300">Total qty {fmt(grandTotal)}</span></span>
            <span>· Value <span className="text-zinc-200">{money(summary.totals?.total_value)}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-2)]">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                  <th className="text-left px-3 py-2">Supplier</th>
                  <th className="text-right px-3 py-2">Returns</th>
                  <th className="text-right px-3 py-2">Total Qty</th>
                  <th className="text-right px-3 py-2">Total Value</th>
                  <th className="text-left px-3 py-2">First Return</th>
                  <th className="text-left px-3 py-2">Last Return</th>
                </tr>
              </thead>
              <tbody>
                {loadingSummary && <tr><td colSpan={6} className="text-center py-6 text-zinc-500">Loading…</td></tr>}
                {!loadingSummary && (summary.rows || []).length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-500">No supplier returns yet.</td></tr>}
                {(summary.rows || []).map((r) => (
                  <tr key={r.supplier_id || r.supplier_name} data-testid={`pr-sum-${r.supplier_id || r.supplier_name}`} className="border-b border-[var(--border)]">
                    <td className="px-3 py-2 text-zinc-200">{r.supplier_name}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{r.returns_count}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(r.total_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-200">{money(r.total_value)}</td>
                    <td className="px-3 py-2 text-zinc-500 font-mono text-[10px]">{r.first_return || "—"}</td>
                    <td className="px-3 py-2 text-zinc-400 font-mono text-[10px]">{r.last_return || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
